import { MASTER_ENDPOINT } from 'utils/Constant';
import Storage from 'utils/Storage';

import { SSEEvent, SSEEventType } from '../constants/sseEvents';

type SSEEventHandler<T = any> = (event: SSEEvent<T>) => void;

interface SSEConfig {
  baseUrl: string;
  getToken: () => string | null;
  retryDelay?: number;
  maxRetryDelay?: number;
  maxRetries?: number;
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: Error) => void;
}

class SseService {
  private controller: AbortController | null = null;
  private listeners = new Map<string, Set<SSEEventHandler>>();
  private wildcardListeners = new Set<SSEEventHandler>();
  private retryCount = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private connected = false;
  private disposed = false;
  private channel: BroadcastChannel;
  private isLeader = false;

  private config: Required<
    Pick<SSEConfig, 'retryDelay' | 'maxRetryDelay' | 'maxRetries'>
  > &
    SSEConfig;

  constructor() {
    this.config = {
      baseUrl: process.env.REACT_APP_APIURL || '',
      getToken: () => {
        const tokens = Storage.getTokens();
        return tokens?.token || null;
      },
      maxRetries: Infinity,
      maxRetryDelay: 30000,
      onConnectionChange: (connected) => {
        console.log(
          `SSE: Connection state changed: ${
            connected ? 'Connected' : 'Disconnected'
          }`,
        );
      },
      onError: (error) => {
        console.error('SSE: Error:', error.message);
      },
      retryDelay: 1000,
    };

    this.channel = new BroadcastChannel('sse_channel');
    this.setupBroadcastListeners();
  }

  // ─── Public API ──────────────────────────────────────────

  async connect(): Promise<void> {
    this.disposed = false;
    const token = this.config.getToken();
    if (!token) return;
    if (this.connected || this.controller) return;

    // Multi-tab optimization: Check if another tab is already the leader
    if (localStorage.getItem('sse_leader') === 'true' && !this.isLeader) {
      console.log(
        'SSE: Another tab is marked as leader in localStorage. Pinging to check if active...',
      );
      const isLeaderActive = await this.checkLeaderActive();
      if (isLeaderActive) {
        console.log(
          'SSE: Active leader found in another tab, listening via BroadcastChannel',
        );
        return;
      }
      console.log(
        'SSE: Stale leader found in localStorage. Claiming leadership.',
      );
      localStorage.removeItem('sse_leader');
    }

    if (!this.isLeader) {
      this.becomeLeader();
    } else {
      this.startConnection();
    }
  }

  disconnect(): void {
    this.cleanup();
    this.retryCount = 0;
    this.setConnected(false);
    if (this.isLeader) {
      localStorage.removeItem('sse_leader');
      this.isLeader = false;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.disconnect();
    this.listeners.clear();
    this.wildcardListeners.clear();
    this.channel.close();
  }

  on<T = any>(
    eventType: SSEEventType,
    handler: SSEEventHandler<T>,
  ): () => void {
    const key = eventType as string;
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(handler as SSEEventHandler);

    return () => {
      this.listeners.get(key)?.delete(handler as SSEEventHandler);
      if (this.listeners.get(key)?.size === 0) {
        this.listeners.delete(key);
      }
    };
  }

  onAny(handler: SSEEventHandler): () => void {
    this.wildcardListeners.add(handler);
    return () => {
      this.wildcardListeners.delete(handler);
    };
  }

  // ─── Internals ───────────────────────────────────────────

  private becomeLeader() {
    this.isLeader = true;
    localStorage.setItem('sse_leader', 'true');
    this.startConnection();

    window.addEventListener('beforeunload', () => {
      if (this.isLeader) {
        localStorage.removeItem('sse_leader');
      }
    });
  }

  private setupBroadcastListeners() {
    this.channel.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);

        // If we are leader and receive a PING_LEADER, reply with PONG_LEADER
        if (this.isLeader && parsed.type === 'PING_LEADER') {
          this.channel.postMessage(JSON.stringify({ type: 'PONG_LEADER' }));
          return;
        }

        // If we are not the leader, dispatch SSE events as normal, but ignore ping/pong messages
        if (!this.isLeader) {
          if (parsed.type === 'PING_LEADER' || parsed.type === 'PONG_LEADER') {
            return;
          }
          const parsedEvent: SSEEvent = parsed;
          this.dispatchEvent(parsedEvent, false); // Don't re-broadcast
        }
      } catch (e) {
        console.error('SSE: Failed to parse broadcast message', e);
      }
    };
  }

  private checkLeaderActive(): Promise<boolean> {
    return new Promise((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this.channel.removeEventListener('message', handleMessage);
          resolve(false);
        }
      }, 500);

      const handleMessage = (event: MessageEvent) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.type === 'PONG_LEADER') {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              this.channel.removeEventListener('message', handleMessage);
              resolve(true);
            }
          }
        } catch (e) {
          // ignore
        }
      };

      this.channel.addEventListener('message', handleMessage);
      this.channel.postMessage(JSON.stringify({ type: 'PING_LEADER' }));
    });
  }

  private async startConnection(): Promise<void> {
    const token = this.config.getToken();
    if (!token) return;

    this.controller = new AbortController();

    try {
      const url = `${this.config.baseUrl}${String(
        MASTER_ENDPOINT.SseSubscribe,
      )}`;
      const response = await fetch(url, {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
        signal: this.controller.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported');
      }

      this.retryCount = 0;
      this.setConnected(true);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done) {
          this.setConnected(false);
          this.scheduleReconnect();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        parts.forEach((part) => this.parseSSEMessage(part));
      }
    } catch (error: any) {
      if (error.name === 'AbortError') return;

      this.setConnected(false);
      this.config.onError?.(error);
      this.controller = null;
      this.scheduleReconnect();
    }
  }

  private parseSSEMessage(raw: string): void {
    const lines = raw.split('\n');
    let data = '';

    lines.forEach((line) => {
      if (line.startsWith('data:')) {
        data += line.slice(5).trim();
      }
    });

    if (!data) return;

    try {
      const parsed = JSON.parse(data);
      // If the top-level object has a type, it's likely the event itself.
      // Otherwise, check if it's wrapped in a 'data' property (common in some frameworks).
      let event: SSEEvent = parsed.type ? parsed : parsed.data ?? parsed;

      if (
        (parsed.job_scored || (parsed.data && parsed.data.job_scored)) &&
        !event.type
      ) {
        event = {
          data: parsed,
          timestamp: Date.now(),
          type: 'job_scored',
        };
      }

      if (event.type) {
        this.dispatchEvent(event, true);
      } else {
        console.warn('SSE: Received message without type:', event);
      }
    } catch (e) {
      // Ignore heartbeat or non-JSON
    }
  }

  private dispatchEvent(event: SSEEvent, broadcast: boolean) {
    // 1. Dispatch to typed listeners
    const handlers = this.listeners.get(event.type);
    if (handlers) {
      handlers.forEach((h) => h(event));
    }

    // 2. Dispatch to wildcard listeners
    this.wildcardListeners.forEach((h) => h(event));

    // 3. Broadcast to other tabs if we are the leader
    if (broadcast && this.isLeader) {
      this.channel.postMessage(JSON.stringify(event));
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed || !this.isLeader) return;

    const delay = Math.min(
      this.config.retryDelay * Math.pow(2, this.retryCount) +
        Math.random() * 1000,
      this.config.maxRetryDelay,
    );

    console.log(`SSE: Reconnecting in ${Math.round(delay / 1000)}s...`);

    this.retryTimeout = setTimeout(() => {
      this.retryCount++;
      this.controller = null;
      this.startConnection();
    }, delay);
  }

  private cleanup(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  private setConnected(value: boolean): void {
    if (this.connected !== value) {
      this.connected = value;
      this.config.onConnectionChange?.(value);
    }
  }

  get isConnected(): boolean {
    return this.connected;
  }
}

export const sseService = new SseService();
